import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  Send, Play, Pause, X, Clock, CheckCircle, XCircle, 
  AlertCircle, RefreshCw, Copy, FileText, ChevronDown, 
  ChevronUp, Users, Calendar, MoreVertical, Loader2,
  History, Rocket, Plus
} from 'lucide-react';

import { getBackendUrl } from '../utils/api';

const API = `${getBackendUrl()}/api`;

// Status das campanhas
const CampaignStatus = {
  PENDING: 'pending',
  RUNNING: 'running',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

const statusConfig = {
  [CampaignStatus.PENDING]: { label: 'Pendente', color: 'text-yellow-400', bg: 'bg-yellow-500/20', icon: Clock },
  [CampaignStatus.RUNNING]: { label: 'Em execução', color: 'text-green-400', bg: 'bg-green-500/20', icon: Play },
  [CampaignStatus.PAUSED]: { label: 'Pausada', color: 'text-orange-400', bg: 'bg-orange-500/20', icon: Pause },
  [CampaignStatus.COMPLETED]: { label: 'Concluída', color: 'text-blue-400', bg: 'bg-blue-500/20', icon: CheckCircle },
  [CampaignStatus.CANCELLED]: { label: 'Cancelada', color: 'text-red-400', bg: 'bg-red-500/20', icon: XCircle },
};

const CampaignsPanel = ({ customers = [], onRefreshCustomers }) => {
  // Estados
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'create' | 'scheduled' | 'history'
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [campaignHistory, setCampaignHistory] = useState([]);
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]); // Campanhas agendadas
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estados para criar campanha
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    message1: 'Olá {nome}! Temos novidades para você! Acesse nosso cardápio: {link}',
    message2: 'Oi {nome}! Confira as promoções de hoje: {link} - {loja}',
    minInterval: 27,
    maxInterval: 45,
    source: 'customers', // 'customers' ou 'csv'
  });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [csvContacts, setCsvContacts] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [csvType, setCsvType] = useState('auto'); // 'auto' | 'full' | 'numbers_only'
  const [creating, setCreating] = useState(false);
  
  // Estados de agendamento
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' | 'schedule'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  
  // Estados de logs em tempo real
  const [showLogs, setShowLogs] = useState(false);
  const [campaignLogs, setCampaignLogs] = useState([]);
  const logsEndRef = useRef(null);
  
  // Polling interval
  const pollingRef = useRef(null);

  // Carregar campanha ativa
  const loadActiveCampaign = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/campaigns/active`);
      setActiveCampaign(response.data);
      
      // Se tem campanha ativa, carregar logs
      if (response.data && response.data.id) {
        const logsResponse = await axios.get(`${API}/campaigns/${response.data.id}/logs?limit=50`);
        setCampaignLogs(logsResponse.data.logs || []);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Erro ao carregar campanha ativa:', err);
      }
      setActiveCampaign(null);
    }
  }, []);

  // Carregar histórico
  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/campaigns/history?limit=50`);
      setCampaignHistory(response.data.campaigns || []);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
      setError('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar campanhas agendadas
  const loadScheduledCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bulk-messages/schedules?status=pending`);
      setScheduledCampaigns(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar agendamentos:', err);
    }
  }, []);

  // Polling para campanha ativa
  useEffect(() => {
    loadActiveCampaign();
    loadHistory();
    loadScheduledCampaigns();
    
    // Iniciar polling se houver campanha em execução
    pollingRef.current = setInterval(() => {
      loadActiveCampaign();
      loadScheduledCampaigns();
    }, 5000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [loadActiveCampaign, loadHistory, loadScheduledCampaigns]);

  // Scroll para o fim dos logs
  useEffect(() => {
    if (showLogs && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [campaignLogs, showLogs]);

  // Criar campanha
  const handleCreateCampaign = async () => {
    const contacts = newCampaign.source === 'csv' ? csvContacts : 
      customers.filter(c => selectedContacts.includes(c.phone)).map(c => ({
        phone: c.phone,
        name: c.name
      }));
    
    if (contacts.length === 0) {
      setError('Selecione pelo menos um contato');
      return;
    }
    
    if (!newCampaign.message1.trim() || !newCampaign.message2.trim()) {
      setError('Preencha as duas mensagens');
      return;
    }

    // Validar agendamento
    if (scheduleMode === 'schedule') {
      if (!scheduleDate || !scheduleTime) {
        setError('Selecione a data e hora do agendamento');
        return;
      }
      
      const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
      const now = new Date();
      
      if (scheduledDateTime <= now) {
        setError('A data/hora do agendamento deve ser no futuro');
        return;
      }
      
      if (scheduledDateTime < new Date(now.getTime() + 5 * 60 * 1000)) {
        setError('O agendamento deve ser com pelo menos 5 minutos de antecedência');
        return;
      }
    }
    
    try {
      setCreating(true);
      setError(null);
      
      if (scheduleMode === 'schedule') {
        // Criar agendamento usando a API de bulk-messages
        const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
        
        const response = await axios.post(`${API}/bulk-messages/schedule`, {
          name: newCampaign.name || `Campanha Agendada ${new Date().toLocaleDateString('pt-BR')}`,
          contacts,
          message1: newCampaign.message1,
          message2: newCampaign.message2,
          minInterval: newCampaign.minInterval,
          maxInterval: newCampaign.maxInterval,
          scheduled_datetime: scheduledDateTime.toISOString()
        });
        
        alert(`✅ ${response.data.message}\n\n📊 ${contacts.length} contatos serão notificados.`);
        setActiveTab('scheduled');
        loadScheduledCampaigns();
      } else {
        // Criar campanha imediata
        const response = await axios.post(`${API}/campaigns/create`, {
          name: newCampaign.name || `Campanha ${new Date().toLocaleDateString('pt-BR')}`,
          contacts,
          message1: newCampaign.message1,
          message2: newCampaign.message2,
          minInterval: newCampaign.minInterval,
          maxInterval: newCampaign.maxInterval
        });
        
        alert('✅ Campanha criada com sucesso!\n\nClique em "Iniciar" para começar o envio.');
        setActiveTab('active');
        loadActiveCampaign();
      }
      
      // Limpar formulário
      setSelectedContacts([]);
      setCsvContacts([]);
      setCsvFile(null);
      setScheduleDate('');
      setScheduleTime('');
      setScheduleMode('now');
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar campanha');
    } finally {
      setCreating(false);
    }
  };

  // Cancelar agendamento
  const handleCancelSchedule = async (scheduleId) => {
    if (!window.confirm('Tem certeza que deseja cancelar este agendamento?')) return;
    
    try {
      await axios.delete(`${API}/bulk-messages/schedules/${scheduleId}`);
      loadScheduledCampaigns();
      alert('Agendamento cancelado com sucesso!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cancelar agendamento');
    }
  };

  // Iniciar/Retomar campanha
  const handleStartCampaign = async (campaignId) => {
    try {
      setError(null);
      await axios.post(`${API}/campaigns/${campaignId}/start`);
      loadActiveCampaign();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao iniciar campanha');
    }
  };

  // Pausar campanha
  const handlePauseCampaign = async (campaignId) => {
    try {
      setError(null);
      await axios.post(`${API}/campaigns/${campaignId}/pause`);
      loadActiveCampaign();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao pausar campanha');
    }
  };

  // Cancelar campanha
  const handleCancelCampaign = async (campaignId) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta campanha? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      setError(null);
      await axios.post(`${API}/campaigns/${campaignId}/cancel`);
      loadActiveCampaign();
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cancelar campanha');
    }
  };

  // Duplicar campanha
  const handleDuplicateCampaign = async (campaignId) => {
    try {
      setError(null);
      await axios.post(`${API}/campaigns/${campaignId}/duplicate`);
      alert('Campanha duplicada com sucesso!');
      loadActiveCampaign();
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao duplicar campanha');
    }
  };

  // Upload CSV
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setCsvFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        alert('Arquivo vazio');
        return;
      }
      
      const contacts = [];
      const firstLine = lines[0].toLowerCase();
      
      // Detectar se tem header
      const hasHeader = firstLine.includes('telefone') || 
                       firstLine.includes('phone') || 
                       firstLine.includes('nome') ||
                       firstLine.includes('name') ||
                       firstLine.includes('numero') ||
                       firstLine.includes('whatsapp');
      
      const startIndex = hasHeader ? 1 : 0;
      
      // Detectar formato automaticamente se csvType é 'auto'
      let isNumbersOnly = csvType === 'numbers_only';
      
      if (csvType === 'auto') {
        // Verificar a primeira linha de dados
        const dataLine = lines[startIndex];
        if (dataLine) {
          const parts = dataLine.split(/[,;\t]/).map(p => p.trim().replace(/"/g, ''));
          // Se tem só 1 coluna ou a segunda coluna parece ser número também, é numbers_only
          const secondCol = parts[1]?.replace(/\D/g, '');
          isNumbersOnly = parts.length === 1 || 
                         (parts.length >= 2 && secondCol && secondCol.length >= 8 && !parts[1].match(/[a-zA-Z]/));
        }
      }
      
      let contactCounter = 1;
      
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Separar por vírgula, ponto-e-vírgula ou tab
        const parts = line.split(/[,;\t]/).map(p => p.trim().replace(/"/g, ''));
        
        if (parts.length > 0 && parts[0]) {
          // Limpar número (remover não-dígitos)
          let phone = parts[0].replace(/\D/g, '');
          
          // Validar tamanho mínimo (pelo menos DDD + número)
          if (phone.length >= 10) {
            // Adicionar código do país se não tiver
            if (phone.length === 10 || phone.length === 11) {
              phone = '55' + phone;
            }
            
            // Determinar o nome
            let name;
            if (isNumbersOnly || !parts[1] || parts[1].replace(/\D/g, '').length >= 8) {
              // Formato apenas números - gerar nome automático
              name = `Contato ${contactCounter}`;
              contactCounter++;
            } else {
              // Formato completo - usar nome da coluna
              name = parts[1] || `Contato ${contactCounter++}`;
            }
            
            contacts.push({
              phone: phone,
              name: name
            });
          }
        }
      }
      
      setCsvContacts(contacts);
      setSelectedContacts(contacts.map(c => c.phone));
      
      if (contacts.length > 0) {
        const formatMsg = isNumbersOnly ? '(formato: apenas números)' : '(formato: nome + telefone)';
        alert(`✅ ${contacts.length} contato(s) carregado(s) ${formatMsg}`);
      } else {
        alert('❌ Nenhum número válido encontrado no arquivo');
      }
    };
    reader.readAsText(file);
  };

  // Toggle seleção
  const toggleSelectAll = () => {
    const contacts = newCampaign.source === 'csv' ? csvContacts : customers;
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.phone));
    }
  };

  const toggleContact = (phone) => {
    if (selectedContacts.includes(phone)) {
      setSelectedContacts(selectedContacts.filter(p => p !== phone));
    } else {
      setSelectedContacts([...selectedContacts, phone]);
    }
  };

  // Formatar data
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calcular progresso
  const getProgress = (campaign) => {
    if (!campaign) return 0;
    const total = campaign.total_contacts || 0;
    const current = (campaign.sent_count || 0) + (campaign.failed_count || 0);
    return total > 0 ? Math.round((current / total) * 100) : 0;
  };

  // Estimar tempo restante
  const getEstimatedTime = (campaign) => {
    if (!campaign) return '-';
    const remaining = campaign.total_contacts - campaign.current_index;
    const avgInterval = (campaign.min_interval + campaign.max_interval) / 2;
    const minutes = Math.ceil((remaining * avgInterval) / 60);
    if (minutes < 1) return 'menos de 1 min';
    if (minutes < 60) return `~${minutes} min`;
    return `~${Math.floor(minutes / 60)}h ${minutes % 60}min`;
  };

  return (
    <div className="space-y-6">
      {/* Tabs - Mobile optimized with horizontal scroll */}
      <div className="flex gap-1 md:gap-2 border-b border-gray-700 pb-2 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
        <button
          onClick={() => setActiveTab('active')}
          data-testid="campaign-tab-active"
          className={`flex items-center gap-1.5 md:gap-2 px-2.5 py-1.5 md:px-4 md:py-2 rounded-t-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
            activeTab === 'active'
              ? 'bg-gray-800 text-white border-b-2 border-green-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Rocket size={14} className="flex-shrink-0" />
          <span className="hidden sm:inline">Campanha</span> Ativa
        </button>
        <button
          onClick={() => setActiveTab('create')}
          data-testid="campaign-tab-create"
          className={`flex items-center gap-1.5 md:gap-2 px-2.5 py-1.5 md:px-4 md:py-2 rounded-t-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
            activeTab === 'create'
              ? 'bg-gray-800 text-white border-b-2 border-purple-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Plus size={14} className="flex-shrink-0" />
          Nova
        </button>
        <button
          onClick={() => { setActiveTab('scheduled'); loadScheduledCampaigns(); }}
          data-testid="campaign-tab-scheduled"
          className={`flex items-center gap-1.5 md:gap-2 px-2.5 py-1.5 md:px-4 md:py-2 rounded-t-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
            activeTab === 'scheduled'
              ? 'bg-gray-800 text-white border-b-2 border-yellow-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Calendar size={14} className="flex-shrink-0" />
          Agendados
          {scheduledCampaigns.length > 0 && (
            <span className="bg-yellow-500 text-black text-xs px-1 py-0.5 rounded-full font-bold leading-none">
              {scheduledCampaigns.length}
            </span>
          )}
        </button>
        <button
          onClick={() => { setActiveTab('history'); loadHistory(); }}
          data-testid="campaign-tab-history"
          className={`flex items-center gap-1.5 md:gap-2 px-2.5 py-1.5 md:px-4 md:py-2 rounded-t-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
            activeTab === 'history'
              ? 'bg-gray-800 text-white border-b-2 border-blue-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <History size={14} className="flex-shrink-0" />
          Histórico
        </button>
      </div>

      {/* Erro */}
      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl flex items-center gap-3">
          <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
          <span className="text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <X size={18} />
          </button>
        </div>
      )}

      {/* ===== CAMPANHA ATIVA ===== */}
      {activeTab === 'active' && (
        <div className="space-y-4">
          {activeCampaign ? (
            <div className="glassmorphism-card rounded-2xl p-4 md:p-6 space-y-4">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    activeCampaign.status === CampaignStatus.RUNNING ? 'bg-green-500 animate-pulse' :
                    activeCampaign.status === CampaignStatus.PAUSED ? 'bg-orange-500' :
                    'bg-gray-500'
                  }`} />
                  <div className="min-w-0">
                    <h3 className="text-base md:text-lg font-semibold text-white truncate">{activeCampaign.name}</h3>
                    <div className={`text-xs md:text-sm ${statusConfig[activeCampaign.status]?.color || 'text-gray-400'}`}>
                      {statusConfig[activeCampaign.status]?.label || activeCampaign.status}
                    </div>
                  </div>
                </div>
                
                {/* Botões de ação */}
                <div className="flex gap-2 flex-shrink-0">
                  {activeCampaign.status === CampaignStatus.PENDING && (
                    <button
                      onClick={() => handleStartCampaign(activeCampaign.id)}
                      className="px-3 py-1.5 md:px-4 md:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs md:text-sm font-medium flex items-center gap-1.5 transition-colors"
                    >
                      <Play size={14} />
                      <span className="hidden sm:inline">Iniciar</span>
                    </button>
                  )}
                  {activeCampaign.status === CampaignStatus.RUNNING && (
                    <button
                      onClick={() => handlePauseCampaign(activeCampaign.id)}
                      className="px-3 py-1.5 md:px-4 md:py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs md:text-sm font-medium flex items-center gap-1.5 transition-colors"
                    >
                      <Pause size={14} />
                      <span className="hidden sm:inline">Pausar</span>
                    </button>
                  )}
                  {activeCampaign.status === CampaignStatus.PAUSED && (
                    <button
                      onClick={() => handleStartCampaign(activeCampaign.id)}
                      className="px-3 py-1.5 md:px-4 md:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs md:text-sm font-medium flex items-center gap-1.5 transition-colors"
                    >
                      <Play size={14} />
                      <span className="hidden sm:inline">Retomar</span>
                    </button>
                  )}
                  {[CampaignStatus.PENDING, CampaignStatus.RUNNING, CampaignStatus.PAUSED].includes(activeCampaign.status) && (
                    <button
                      onClick={() => handleCancelCampaign(activeCampaign.id)}
                      className="px-3 py-1.5 md:px-4 md:py-2 bg-red-600/80 hover:bg-red-600 text-white rounded-lg text-xs md:text-sm font-medium flex items-center gap-1.5 transition-colors"
                    >
                      <X size={14} />
                      <span className="hidden sm:inline">Cancelar</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Barra de progresso */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Progresso</span>
                  <span className="text-white font-medium">
                    {activeCampaign.current_index || 0}/{activeCampaign.total_contacts} ({getProgress(activeCampaign)}%)
                  </span>
                </div>
                <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      activeCampaign.status === CampaignStatus.RUNNING 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                        : 'bg-gradient-to-r from-blue-500 to-indigo-400'
                    }`}
                    style={{ width: `${getProgress(activeCampaign)}%` }}
                  />
                </div>
              </div>

              {/* Estatísticas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                <div className="bg-gray-800/50 rounded-xl p-2 md:p-3 text-center">
                  <div className="text-xl md:text-2xl font-bold text-green-400">{activeCampaign.sent_count || 0}</div>
                  <div className="text-xs text-gray-400">Enviadas</div>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-2 md:p-3 text-center">
                  <div className="text-xl md:text-2xl font-bold text-red-400">{activeCampaign.failed_count || 0}</div>
                  <div className="text-xs text-gray-400">Falhas</div>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-2 md:p-3 text-center">
                  <div className="text-xl md:text-2xl font-bold text-yellow-400">
                    {activeCampaign.total_contacts - (activeCampaign.current_index || 0)}
                  </div>
                  <div className="text-xs text-gray-400">Pendentes</div>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-2 md:p-3 text-center">
                  <div className="text-sm md:text-lg font-bold text-blue-400">{getEstimatedTime(activeCampaign)}</div>
                  <div className="text-xs text-gray-400">Restante</div>
                </div>
              </div>

              {/* Último envio */}
              {activeCampaign.last_phone && (
                <div className="text-sm text-gray-400">
                  Último: <span className="text-white">{activeCampaign.last_phone}</span>
                </div>
              )}

              {/* Logs em tempo real */}
              <div>
                <button
                  onClick={() => setShowLogs(!showLogs)}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {showLogs ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {showLogs ? 'Ocultar detalhes' : 'Ver detalhes do envio'}
                </button>
                
                {showLogs && (
                  <div className="mt-3 max-h-64 overflow-y-auto bg-gray-900/50 rounded-xl p-3 space-y-1 font-mono text-xs">
                    {campaignLogs.length === 0 ? (
                      <div className="text-gray-500 text-center py-4">Nenhum log ainda</div>
                    ) : (
                      campaignLogs.map((log, idx) => (
                        <div 
                          key={idx} 
                          className={`flex items-center gap-2 py-1 ${
                            log.status === 'sent' ? 'text-green-400' : 
                            log.status === 'failed' ? 'text-red-400' : 'text-gray-400'
                          }`}
                        >
                          <span className="text-gray-500 w-16 flex-shrink-0">
                            {new Date(log.time).toLocaleTimeString('pt-BR')}
                          </span>
                          {log.status === 'sent' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                          <span className="flex-shrink-0">{log.phone}</span>
                          <span className="text-gray-500 truncate">- {log.name}</span>
                          {log.error && <span className="text-red-400 ml-2">({log.error})</span>}
                        </div>
                      ))
                    )}
                    <div ref={logsEndRef} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="glassmorphism-card rounded-2xl p-6 md:p-12 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 bg-gray-800 rounded-full flex items-center justify-center">
                <Send className="text-gray-500" size={24} />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">Nenhuma campanha ativa</h3>
              <p className="text-gray-400 text-sm md:text-base mb-6">Crie uma nova campanha para enviar mensagens</p>
              <button
                onClick={() => setActiveTab('create')}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium inline-flex items-center gap-2 transition-colors"
              >
                <Plus size={20} />
                Nova Campanha
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== CRIAR CAMPANHA ===== */}
      {activeTab === 'create' && (
        <div className="space-y-6">
          {/* Nome da campanha */}
          <div className="glassmorphism-card rounded-2xl p-4 md:p-6 space-y-4">
            <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
              <FileText size={18} />
              Informações da Campanha
            </h3>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">Nome da campanha</label>
              <input
                type="text"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                placeholder={`Campanha ${new Date().toLocaleDateString('pt-BR')}`}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          {/* Mensagens */}
          <div className="glassmorphism-card rounded-2xl p-4 md:p-6 space-y-4">
            <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
              <Send size={18} />
              Mensagens (alternadas)
            </h3>
            
            <div className="text-sm text-gray-400 bg-gray-800/30 rounded-lg p-3">
              Variáveis disponíveis: <code className="text-purple-400">{'{nome}'}</code>, 
              <code className="text-purple-400 ml-2">{'{link}'}</code>, 
              <code className="text-purple-400 ml-2">{'{loja}'}</code>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Mensagem 1</label>
                <textarea
                  value={newCampaign.message1}
                  onChange={(e) => setNewCampaign({...newCampaign, message1: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Mensagem 2</label>
                <textarea
                  value={newCampaign.message2}
                  onChange={(e) => setNewCampaign({...newCampaign, message2: e.target.value})}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                />
              </div>
            </div>

            {/* Intervalo */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Intervalo mínimo (segundos)</label>
                <input
                  type="number"
                  min="27"
                  value={newCampaign.minInterval}
                  onChange={(e) => setNewCampaign({...newCampaign, minInterval: Math.max(27, parseInt(e.target.value) || 27)})}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Intervalo máximo (segundos)</label>
                <input
                  type="number"
                  min="27"
                  value={newCampaign.maxInterval}
                  onChange={(e) => setNewCampaign({...newCampaign, maxInterval: Math.max(newCampaign.minInterval, parseInt(e.target.value) || 45)})}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Contatos */}
          <div className="glassmorphism-card rounded-2xl p-4 md:p-6 space-y-4">
            <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
              <Users size={18} />
              Contatos
            </h3>

            {/* Fonte dos contatos */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={newCampaign.source === 'customers'}
                  onChange={() => setNewCampaign({...newCampaign, source: 'customers'})}
                  className="w-4 h-4 text-purple-500"
                />
                <span className="text-gray-300">Clientes cadastrados ({customers.length})</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={newCampaign.source === 'csv'}
                  onChange={() => setNewCampaign({...newCampaign, source: 'csv'})}
                  className="w-4 h-4 text-purple-500"
                />
                <span className="text-gray-300">Importar CSV</span>
              </label>
            </div>

            {/* Upload CSV */}
            {newCampaign.source === 'csv' && (
              <div className="space-y-4 p-4 bg-gray-800/30 rounded-xl">
                {/* Seletor de tipo de CSV */}
                <div className="space-y-2">
                  <label className="text-sm text-gray-400">Formato do arquivo:</label>
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center gap-2 cursor-pointer bg-gray-700/50 px-3 py-2 rounded-lg hover:bg-gray-700">
                      <input
                        type="radio"
                        checked={csvType === 'auto'}
                        onChange={() => setCsvType('auto')}
                        className="w-4 h-4 text-purple-500"
                      />
                      <span className="text-gray-300 text-sm">Detectar automático</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer bg-gray-700/50 px-3 py-2 rounded-lg hover:bg-gray-700">
                      <input
                        type="radio"
                        checked={csvType === 'full'}
                        onChange={() => setCsvType('full')}
                        className="w-4 h-4 text-purple-500"
                      />
                      <span className="text-gray-300 text-sm">Nome + Telefone</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer bg-gray-700/50 px-3 py-2 rounded-lg hover:bg-gray-700">
                      <input
                        type="radio"
                        checked={csvType === 'numbers_only'}
                        onChange={() => setCsvType('numbers_only')}
                        className="w-4 h-4 text-purple-500"
                      />
                      <span className="text-gray-300 text-sm">Apenas Números</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    {csvType === 'auto' && '🔍 O sistema detectará automaticamente o formato do arquivo'}
                    {csvType === 'full' && '📋 Formato: telefone,nome (ex: 5538999991111,João)'}
                    {csvType === 'numbers_only' && '📱 Formato: um número por linha (ex: 5538999991111)'}
                  </p>
                </div>

                {/* Botão de upload */}
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleCsvUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer flex items-center gap-2"
                  >
                    <FileText size={18} />
                    {csvFile ? csvFile.name : 'Selecionar arquivo'}
                  </label>
                  {csvContacts.length > 0 && (
                    <span className="text-green-400">{csvContacts.length} contatos carregados</span>
                  )}
                </div>

                {/* Exemplo de formato */}
                {!csvFile && (
                  <div className="bg-gray-900/50 rounded-lg p-3 text-xs">
                    <p className="text-gray-400 mb-2">Exemplo de arquivo aceito:</p>
                    {csvType === 'numbers_only' ? (
                      <pre className="text-green-400 font-mono">
{`5538999991111
5538999992222
5538999993333`}
                      </pre>
                    ) : csvType === 'full' ? (
                      <pre className="text-green-400 font-mono">
{`telefone,nome
5538999991111,João Silva
5538999992222,Maria Santos`}
                      </pre>
                    ) : (
                      <pre className="text-green-400 font-mono">
{`# Ambos formatos são aceitos:
5538999991111
ou
5538999991111,João Silva`}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Lista de contatos */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <button
                  onClick={toggleSelectAll}
                  className="text-sm text-purple-400 hover:text-purple-300"
                >
                  {selectedContacts.length === (newCampaign.source === 'csv' ? csvContacts : customers).length
                    ? 'Desmarcar todos'
                    : 'Selecionar todos'}
                </button>
                <span className="text-sm text-gray-400">
                  {selectedContacts.length} selecionado(s)
                </span>
              </div>
              
              <div className="max-h-64 overflow-y-auto bg-gray-800/30 rounded-xl p-2 space-y-1">
                {(newCampaign.source === 'csv' ? csvContacts : customers).map((contact) => (
                  <label
                    key={contact.phone}
                    className="flex items-center gap-3 p-2 hover:bg-gray-700/50 rounded-lg cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(contact.phone)}
                      onChange={() => toggleContact(contact.phone)}
                      className="w-4 h-4 text-purple-500 rounded"
                    />
                    <span className="text-white">{contact.name}</span>
                    <span className="text-gray-500 text-sm">{contact.phone}</span>
                  </label>
                ))}
                {(newCampaign.source === 'csv' ? csvContacts : customers).length === 0 && (
                  <div className="text-gray-500 text-center py-8">
                    {newCampaign.source === 'csv' 
                      ? 'Carregue um arquivo CSV' 
                      : 'Nenhum cliente cadastrado'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Opções de Agendamento */}
          <div className="glassmorphism-card p-4 rounded-xl space-y-4">
            <h4 className="font-medium text-white flex items-center gap-2">
              <Clock size={18} className="text-yellow-400" />
              Quando enviar?
            </h4>
            
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Opção: Iniciar Agora */}
              <label 
                className={`flex-1 flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all border-2 ${
                  scheduleMode === 'now' 
                    ? 'bg-green-500/20 border-green-500' 
                    : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                }`}
              >
                <input
                  type="radio"
                  checked={scheduleMode === 'now'}
                  onChange={() => setScheduleMode('now')}
                  className="sr-only"
                />
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  scheduleMode === 'now' ? 'bg-green-500' : 'bg-gray-700'
                }`}>
                  <Rocket size={20} className="text-white" />
                </div>
                <div>
                  <div className="font-medium text-white">Iniciar Agora</div>
                  <div className="text-sm text-gray-400">Criar e iniciar manualmente</div>
                </div>
              </label>
              
              {/* Opção: Agendar */}
              <label 
                className={`flex-1 flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all border-2 ${
                  scheduleMode === 'schedule' 
                    ? 'bg-yellow-500/20 border-yellow-500' 
                    : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                }`}
              >
                <input
                  type="radio"
                  checked={scheduleMode === 'schedule'}
                  onChange={() => setScheduleMode('schedule')}
                  className="sr-only"
                />
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  scheduleMode === 'schedule' ? 'bg-yellow-500' : 'bg-gray-700'
                }`}>
                  <Calendar size={20} className={scheduleMode === 'schedule' ? 'text-black' : 'text-white'} />
                </div>
                <div>
                  <div className="font-medium text-white">Agendar para Depois</div>
                  <div className="text-sm text-gray-400">Definir data e hora</div>
                </div>
              </label>
            </div>
            
            {/* Campos de Data/Hora (só aparecem se for agendamento) */}
            {scheduleMode === 'schedule' && (
              <div className="mt-4 p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/30 space-y-4">
                <div className="flex items-center gap-2 text-yellow-400 text-sm">
                  <AlertCircle size={16} />
                  <span>A campanha será iniciada automaticamente na data/hora selecionada</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Data</label>
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Hora</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                    />
                  </div>
                </div>
                
                {scheduleDate && scheduleTime && (
                  <div className="text-center p-3 bg-yellow-500/20 rounded-lg">
                    <span className="text-yellow-300">
                      📅 Agendado para: <strong>{new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString('pt-BR')}</strong>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Botão criar */}
          <button
            onClick={handleCreateCampaign}
            disabled={creating || selectedContacts.length === 0}
            data-testid="campaign-create-btn"
            className={`w-full py-3 md:py-4 text-sm md:text-base ${
              scheduleMode === 'schedule'
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600'
                : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
            } disabled:from-gray-600 disabled:to-gray-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all`}
          >
            {creating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                {scheduleMode === 'schedule' ? 'Agendando...' : 'Criando...'}
              </>
            ) : (
              <>
                {scheduleMode === 'schedule' ? (
                  <>
                    <Calendar size={20} />
                    Agendar Campanha ({selectedContacts.length} contatos)
                  </>
                ) : (
                  <>
                    <Rocket size={20} />
                    Criar Campanha ({selectedContacts.length} contatos)
                  </>
                )}
              </>
            )}
          </button>
        </div>
      )}

      {/* ===== CAMPANHAS AGENDADAS ===== */}
      {activeTab === 'scheduled' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="text-yellow-400" size={18} />
              Campanhas Agendadas
            </h3>
            <button
              onClick={loadScheduledCampaigns}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {scheduledCampaigns.length === 0 ? (
            <div className="glassmorphism-card rounded-2xl p-6 md:p-12 text-center">
              <Calendar className="text-gray-500 mx-auto mb-4" size={36} />
              <p className="text-gray-400 text-sm md:text-base mb-2">Nenhuma campanha agendada</p>
              <p className="text-gray-500 text-xs md:text-sm">Crie uma campanha e selecione &quot;Agendar&quot;</p>
              <button
                onClick={() => setActiveTab('create')}
                className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium inline-flex items-center gap-2"
              >
                <Plus size={18} />
                Nova Campanha
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduledCampaigns.map((schedule) => {
                const scheduleDate = new Date(schedule.scheduled_datetime);
                const now = new Date();
                const diffMs = scheduleDate - now;
                const diffMinutes = Math.floor(diffMs / 60000);
                const diffHours = Math.floor(diffMinutes / 60);
                const diffDays = Math.floor(diffHours / 24);
                
                let timeUntil = '';
                if (diffDays > 0) {
                  timeUntil = `em ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
                } else if (diffHours > 0) {
                  timeUntil = `em ${diffHours}h ${diffMinutes % 60}min`;
                } else if (diffMinutes > 0) {
                  timeUntil = `em ${diffMinutes} minutos`;
                } else {
                  timeUntil = 'em breve';
                }
                
                return (
                  <div
                    key={schedule.id}
                    className="glassmorphism-card rounded-xl p-3 md:p-4 border border-yellow-500/30 hover:border-yellow-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 md:gap-4 min-w-0">
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                          <Clock className="text-yellow-400" size={20} />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-medium text-white text-sm md:text-base truncate">{schedule.name || 'Campanha Agendada'}</h4>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
                            <span className="text-yellow-400 font-medium flex items-center gap-1">
                              <Calendar size={14} />
                              {scheduleDate.toLocaleDateString('pt-BR')} às {scheduleDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className="text-gray-400 text-sm">({timeUntil})</span>
                          </div>
                          <div className="text-sm text-gray-400 mt-1">
                            <Users size={14} className="inline mr-1" />
                            {schedule.total_contacts || schedule.contacts?.length || 0} contatos
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCancelSchedule(schedule.id)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Cancelar agendamento"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Preview das mensagens */}
                    <div className="mt-4 pt-4 border-t border-gray-700/50">
                      <div className="text-xs text-gray-500 mb-2">Preview da mensagem:</div>
                      <div className="text-sm text-gray-300 bg-gray-800/50 rounded-lg p-3 line-clamp-2">
                        {schedule.message1 || 'Mensagem não disponível'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== HISTÓRICO ===== */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Histórico de Campanhas</h3>
            <button
              onClick={loadHistory}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader2 size={32} className="animate-spin text-purple-400 mx-auto" />
            </div>
          ) : campaignHistory.length === 0 ? (
            <div className="glassmorphism-card rounded-2xl p-6 md:p-12 text-center">
              <History className="text-gray-500 mx-auto mb-4" size={36} />
              <p className="text-gray-400 text-sm md:text-base">Nenhuma campanha encontrada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {campaignHistory.map((campaign) => {
                const StatusIcon = statusConfig[campaign.status]?.icon || Clock;
                return (
                  <div
                    key={campaign.id}
                    className="glassmorphism-card rounded-xl p-3 md:p-4 hover:bg-gray-800/30 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 md:gap-4 min-w-0">
                        <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg flex-shrink-0 ${statusConfig[campaign.status]?.bg || 'bg-gray-500/20'} flex items-center justify-center`}>
                          <StatusIcon size={16} className={statusConfig[campaign.status]?.color || 'text-gray-400'} />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-medium text-white text-sm md:text-base truncate">{campaign.name}</h4>
                          <div className="text-xs md:text-sm text-gray-400 truncate">
                            {formatDate(campaign.created_at)} • {campaign.total_contacts} contatos
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right hidden sm:block">
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-green-400">{campaign.sent_count || 0} enviadas</span>
                            <span className="text-red-400">{campaign.failed_count || 0} falhas</span>
                          </div>
                          <div className={`text-sm ${statusConfig[campaign.status]?.color || 'text-gray-400'}`}>
                            {statusConfig[campaign.status]?.label || campaign.status}
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleDuplicateCampaign(campaign.id)}
                          className="p-2 text-gray-400 hover:text-purple-400 transition-colors"
                          title="Duplicar campanha"
                        >
                          <Copy size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignsPanel;
